import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';

const CaseVisa = ({ visaApps, visaDocProgress, id, caseRec, onReload }) => {
    const navigate = useNavigate();
    const [viewingVisa, setViewingVisa] = useState(null);
    const [editingVisa, setEditingVisa] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);

    const handleView = (visa) => {
        setViewingVisa(visa);
    };

    const handleEdit = (visa) => {
        setEditingVisa(visa);
        setEditForm({
            status: visa.status || 'draft',
            submitted_at: visa.submitted_at ? visa.submitted_at.split('T')[0] : '',
            biometrics_at: visa.biometrics_at ? visa.biometrics_at.split('T')[0] : '',
            portal_ref: visa.metadata?.portal_ref || '',
            notes: visa.metadata?.notes || ''
        });
    };

    const handleSaveEdit = async () => {
        if (!editingVisa) return;
        setSaving(true);
        try {
            await supabase.from('visa_applications').update({
                status: editForm.status,
                submitted_at: editForm.submitted_at || null,
                biometrics_at: editForm.biometrics_at || null,
                metadata: {
                    ...editingVisa.metadata,
                    portal_ref: editForm.portal_ref || null,
                    notes: editForm.notes || null
                }
            }).eq('id', editingVisa.id);
            setEditingVisa(null);
            if (onReload) onReload();
        } catch (e) {
            console.error('Failed to update visa application:', e);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (visaId) => {
        if (!window.confirm('Are you sure you want to delete this visa application?')) return;
        try {
            await supabase.from('visa_applications').delete().eq('id', visaId);
            if (onReload) onReload();
        } catch (e) {
            console.error('Failed to delete visa application:', e);
        }
    };

    const statusColors = {
        draft: 'bg-gray-700 text-gray-400 border-gray-600',
        submitted: 'bg-blue-900/30 text-blue-400 border-blue-800',
        biometrics_scheduled: 'bg-purple-900/30 text-purple-400 border-purple-800',
        approved: 'bg-green-900/30 text-green-400 border-green-800',
        rejected: 'bg-red-900/30 text-red-400 border-red-800'
    };

    return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
            <div className="bg-gray-900/50 p-4 border-b border-gray-700 flex justify-between items-center">
                <h4 className="text-lg font-bold text-cyan-400 uppercase tracking-wider flex items-center gap-2">
                    🛂 VISA APPLICATIONS
                </h4>
                <Link to={`/admin/visa_apps/new?case_id=${id}${caseRec?.client_id ? `&client_id=${caseRec.client_id}` : ''}`} className="px-3 py-1.5 rounded text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 uppercase tracking-wider border border-blue-500">
                    + New Visa
                </Link>
            </div>

            {/* View Modal */}
            {viewingVisa && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setViewingVisa(null)}>
                    <div className="bg-gray-800 border border-gray-600 rounded-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
                        <h3 className="text-xl font-bold text-white mb-4">Visa Application Details</h3>
                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between border-b border-gray-700 pb-2">
                                <span className="text-gray-400">Country</span>
                                <span className="text-white font-bold">{viewingVisa.visa_products?.country || viewingVisa.metadata?.country || '-'}</span>
                            </div>
                            <div className="flex justify-between border-b border-gray-700 pb-2">
                                <span className="text-gray-400">Visa Type</span>
                                <span className="text-white">{viewingVisa.visa_products?.visa_type || viewingVisa.metadata?.visa_type || '-'}</span>
                            </div>
                            <div className="flex justify-between border-b border-gray-700 pb-2">
                                <span className="text-gray-400">Status</span>
                                <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase border ${statusColors[viewingVisa.status] || statusColors.draft}`}>{viewingVisa.status || 'draft'}</span>
                            </div>
                            <div className="flex justify-between border-b border-gray-700 pb-2">
                                <span className="text-gray-400">Submitted</span>
                                <span className="text-white">{viewingVisa.submitted_at ? new Date(viewingVisa.submitted_at).toLocaleDateString() : '-'}</span>
                            </div>
                            <div className="flex justify-between border-b border-gray-700 pb-2">
                                <span className="text-gray-400">Biometrics</span>
                                <span className="text-white">{viewingVisa.biometrics_at ? new Date(viewingVisa.biometrics_at).toLocaleDateString() : '-'}</span>
                            </div>
                            <div className="flex justify-between border-b border-gray-700 pb-2">
                                <span className="text-gray-400">Applicant</span>
                                <span className="text-white">{viewingVisa.metadata?.applicant_name || '-'}</span>
                            </div>
                            <div className="flex justify-between border-b border-gray-700 pb-2">
                                <span className="text-gray-400">Passport</span>
                                <span className="text-white font-mono">{viewingVisa.metadata?.passport_number || '-'}</span>
                            </div>
                            {viewingVisa.metadata?.portal_ref && (
                                <div className="flex justify-between border-b border-gray-700 pb-2">
                                    <span className="text-gray-400">Portal Ref</span>
                                    <span className="text-white font-mono">{viewingVisa.metadata.portal_ref}</span>
                                </div>
                            )}
                            {viewingVisa.metadata?.notes && (
                                <div className="border-b border-gray-700 pb-2">
                                    <span className="text-gray-400 block mb-1">Notes</span>
                                    <p className="text-white text-xs">{viewingVisa.metadata.notes}</p>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2 mt-4">
                            <button onClick={() => setViewingVisa(null)} className="flex-1 px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600">Close</button>
                            <button onClick={() => navigate(`/admin/visa_apps/${viewingVisa.id}`)} className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-500">Full Details</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {editingVisa && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setEditingVisa(null)}>
                    <div className="bg-gray-800 border border-gray-600 rounded-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
                        <h3 className="text-xl font-bold text-white mb-4">Edit Visa Application</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Status</label>
                                <select className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white" value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })}>
                                    <option value="draft">Draft</option>
                                    <option value="submitted">Submitted</option>
                                    <option value="biometrics_scheduled">Biometrics Scheduled</option>
                                    <option value="approved">Approved</option>
                                    <option value="rejected">Rejected</option>
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Submitted At</label>
                                    <input type="date" className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white" value={editForm.submitted_at} onChange={e => setEditForm({ ...editForm, submitted_at: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Biometrics At</label>
                                    <input type="date" className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white" value={editForm.biometrics_at} onChange={e => setEditForm({ ...editForm, biometrics_at: e.target.value })} />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Portal Reference</label>
                                <input type="text" className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white" value={editForm.portal_ref} onChange={e => setEditForm({ ...editForm, portal_ref: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Notes</label>
                                <textarea rows={3} className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white" value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} />
                            </div>
                        </div>
                        <div className="flex gap-2 mt-4">
                            <button onClick={() => setEditingVisa(null)} className="flex-1 px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600">Cancel</button>
                            <button onClick={handleSaveEdit} disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 disabled:opacity-50">{saving ? 'Saving...' : 'Save'}</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="w-full overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-900 border-b border-gray-700 text-xs font-bold text-gray-500 uppercase tracking-wider">
                            <th className="p-4">Type • Country</th>
                            <th className="p-4">Status</th>
                            <th className="p-4">Submitted</th>
                            <th className="p-4">Checklist</th>
                            <th className="p-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700 font-mono text-sm">
                        {visaApps.map(v => (
                            <tr key={v.id} className="hover:bg-gray-700/30 transition-colors">
                                <td className="p-4 text-white">
                                    <div className="font-bold">{v.visa_products?.country || v.metadata?.country || 'Unknown Country'}</div>
                                    <div className="text-xs text-gray-400 mt-1">{v.visa_products?.visa_type || v.metadata?.visa_type || 'Unknown Type'}</div>
                                </td>
                                <td className="p-4">
                                    <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase border ${statusColors[v.status] || statusColors.draft}`}>
                                        {v.status || 'draft'}
                                    </span>
                                </td>
                                <td className="p-4 text-gray-300">{v.submitted_at ? new Date(v.submitted_at).toLocaleDateString() : '-'}</td>
                                <td className="p-4">
                                    {(() => {
                                        const p = visaDocProgress[v.id] || { done: 0, total: 0 };
                                        return (
                                            <div className="flex items-center gap-2">
                                                <div className="w-16 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                                                    <div className="h-full bg-cyan-500" style={{ width: `${p.total ? (p.done / p.total) * 100 : 0}%` }}></div>
                                                </div>
                                                <span className="text-xs text-gray-400">{p.done}/{p.total}</span>
                                            </div>
                                        );
                                    })()}
                                </td>
                                <td className="p-4">
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => handleView(v)} className="px-2 py-1 rounded text-xs font-bold bg-gray-700 text-white border border-gray-600 hover:bg-gray-600">View</button>
                                        <button onClick={() => handleEdit(v)} className="px-2 py-1 rounded text-xs font-bold bg-blue-600 text-white border border-blue-500 hover:bg-blue-500">Edit</button>
                                        <button onClick={() => navigate(`/admin/visa_apps/${v.id}`)} className="px-2 py-1 rounded text-xs font-bold bg-cyan-600 text-white border border-cyan-500 hover:bg-cyan-500">Details</button>
                                        <button onClick={() => handleDelete(v.id)} className="px-2 py-1 rounded text-xs font-bold bg-red-900/50 text-red-400 border border-red-800 hover:bg-red-900">Delete</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {visaApps.length === 0 && (
                            <tr>
                                <td colSpan="5" className="p-8 text-center text-gray-500 uppercase">No visa applications found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CaseVisa;
